import { QTY_LIMITS, isWholeNumber, roundQty } from "./quantity";

export const CONVERSION_FACTOR_MAX = 32767;

export type ConversionKind = "KIT" | "DE-KIT" | "SWAP";

export type ConversionBlocker = {
	id: string;
	key: string;
	params?: Record<string, string | number>;
};

export const isConversionKind = (value: unknown): value is ConversionKind =>
	value === "KIT" || value === "DE-KIT" || value === "SWAP";

export const effectiveFactor = (kind: ConversionKind | "" | undefined, factor: number): number => {
	if (kind === "SWAP") return 1;
	return Number.isFinite(factor) && factor > 0 ? Math.floor(factor) : 0;
};

/**
 * Pack-size physics: factor is units in one pack.
 * KIT:    many source → one result  (bottles → carton)
 * DE-KIT: one source → many result  (carton → bottles)
 * SWAP:   1:1
 */
export const produceFromSource = (
	kind: ConversionKind | "" | undefined,
	sourceQty: number,
	factor: number,
): number => {
	const f = effectiveFactor(kind, factor);
	if (!kind || f < 1 || !Number.isFinite(sourceQty)) return NaN;
	if (kind === "KIT") return sourceQty / f;
	if (kind === "DE-KIT") return sourceQty * f;
	return sourceQty;
};

export const sourceFromProduce = (
	kind: ConversionKind | "" | undefined,
	produceQty: number,
	factor: number,
): number => {
	const f = effectiveFactor(kind, factor);
	if (!kind || f < 1 || !Number.isFinite(produceQty)) return NaN;
	if (kind === "KIT") return produceQty * f;
	if (kind === "DE-KIT") return produceQty / f;
	return produceQty;
};

/**
 * The number in the quantity stepper:
 * KIT/SWAP — how many result items
 * DE-KIT   — how many source packs to split
 */
export const inputQtyFromStored = (
	kind: ConversionKind | "" | undefined,
	sourceQty: number,
	produceQty: number,
	factor: number,
): number => {
	if (kind === "DE-KIT") {
		const src = Number(sourceQty);
		if (Number.isFinite(src) && src > 0) return roundQty(src);
		const fromProduce = sourceFromProduce(kind, Number(produceQty), factor);
		if (Number.isFinite(fromProduce) && fromProduce > 0) return roundQty(fromProduce);
		return 1;
	}
	const produced = Number(produceQty);
	if (Number.isFinite(produced) && produced > 0) return roundQty(produced);
	const derived = produceFromSource(kind, Number(sourceQty), factor);
	return Number.isFinite(derived) && derived > 0 ? roundQty(derived) : 1;
};

export const quantitiesFromInput = (
	kind: ConversionKind | "" | undefined,
	inputQty: number,
	factor: number,
): { sourceQty: number; produceQty: number } => {
	const f = effectiveFactor(kind, factor);
	const n = Number(inputQty);
	if (!kind || f < 1 || !Number.isFinite(n) || n <= 0) {
		return { sourceQty: NaN, produceQty: NaN };
	}
	if (kind === "KIT") return { sourceQty: roundQty(n * f), produceQty: roundQty(n) };
	if (kind === "DE-KIT") return { sourceQty: roundQty(n), produceQty: roundQty(n * f) };
	return { sourceQty: roundQty(n), produceQty: roundQty(n) };
};

export const maxInputQty = (
	kind: ConversionKind | "" | undefined,
	sourceStock: number,
	factor: number,
	unsignedMax: number = QTY_LIMITS.max,
	fractional?: boolean,
): number => {
	const f = effectiveFactor(kind, factor);
	if (!kind || f < 1) return unsignedMax;
	if (kind === "KIT") {
		const raw = sourceStock / f;
		const capped = fractional ? roundQty(raw) : Math.floor(raw);
		return Math.min(unsignedMax, capped);
	}
	return Math.min(unsignedMax, sourceStock);
};

export const getConversionBlockers = (input: {
	convType?: string;
	receiver?: number;
	depot?: number;
	sourceItem?: number;
	destItem?: number;
	inputQty?: number;
	factor?: number;
	sourceCost?: number;
	destCost?: number;
	sourceStock?: number;
	enforceSourceStock?: boolean;
	itemsReady?: boolean;
	clientsReady?: boolean;
	depotsReady?: boolean;
	loading?: boolean;
	unsignedMax?: number;
	fractional?: boolean;
}): ConversionBlocker[] => {
	const blockers: ConversionBlocker[] = [];
	const kind = isConversionKind(input.convType) ? input.convType : "";
	const factor = effectiveFactor(kind, Number(input.factor));
	const inputQty = Number(input.inputQty);
	const unsignedMax = input.unsignedMax ?? QTY_LIMITS.max;
	const { sourceQty, produceQty } = quantitiesFromInput(kind, inputQty, factor);

	if (input.loading) {
		blockers.push({ id: "loading", key: "conversion.blockers.loading" });
	}
	if (!kind) {
		blockers.push({ id: "type", key: "conversion.blockers.pickType" });
	}
	if (input.clientsReady !== false && !(Number(input.receiver) > 0)) {
		blockers.push({ id: "observer", key: "conversion.blockers.pickObserver" });
	}
	if (input.depotsReady !== false && !(Number(input.depot) > 0)) {
		blockers.push({ id: "depot", key: "conversion.blockers.pickDepot" });
	}
	if (input.itemsReady !== false && !(Number(input.sourceItem) > 0)) {
		blockers.push({ id: "source", key: "conversion.blockers.pickSource" });
	}
	if (input.itemsReady !== false && !(Number(input.destItem) > 0)) {
		blockers.push({ id: "dest", key: "conversion.blockers.pickDest" });
	}
	if (
		Number(input.sourceItem) > 0
		&& Number(input.destItem) > 0
		&& Number(input.sourceItem) === Number(input.destItem)
	) {
		blockers.push({ id: "same", key: "conversion.blockers.sameItem" });
	}
	if (!Number.isFinite(inputQty) || inputQty <= 0 || inputQty > unsignedMax) {
		blockers.push({ id: "produce", key: "conversion.blockers.produceQty" });
	} else if (input.fractional === false && !isWholeNumber(inputQty)) {
		blockers.push({ id: "produce", key: "conversion.blockers.produceQty" });
	}
	if (kind && kind !== "SWAP" && (!Number.isInteger(factor) || factor < 1 || factor > CONVERSION_FACTOR_MAX)) {
		blockers.push({ id: "factor", key: "conversion.blockers.factor" });
	}
	if (
		kind
		&& kind !== "SWAP"
		&& Number.isFinite(inputQty)
		&& inputQty > 0
		&& factor > 0
		&& roundQty(inputQty * factor) > unsignedMax
	) {
		blockers.push({ id: "packOverflow", key: "conversion.blockers.packOverflow" });
	}
	if (
		input.enforceSourceStock
		&& Number.isFinite(sourceQty)
		&& sourceQty > Number(input.sourceStock ?? 0)
	) {
		blockers.push({
			id: "stock",
			key: "conversion.blockers.insufficientStock",
			params: {
				required: sourceQty,
				available: Number(input.sourceStock ?? 0),
			},
		});
	}
	if (Number.isFinite(Number(input.sourceCost)) === false || Number(input.sourceCost) < 0) {
		blockers.push({ id: "sourceCost", key: "conversion.blockers.sourceCost" });
	}
	if (Number.isFinite(Number(input.destCost)) === false || Number(input.destCost) < 0) {
		blockers.push({ id: "destCost", key: "conversion.blockers.destCost" });
	}
	if (Number.isFinite(produceQty) && produceQty > unsignedMax) {
		blockers.push({ id: "packOverflow", key: "conversion.blockers.packOverflow" });
	}
	return blockers;
};
